import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import qs from 'query-string';
import { dataCentralApi } from '@/api/client';
import { toFilters } from '@/api/customers';
import type { ResponseArray, ResponseObject } from '@/types/shared';

/**
 * Resellers — field names and endpoint paths lifted verbatim from the backend
 * `modules/reseller` Go types and verified against live staging.
 * Shapes confirmed via `node scripts/probe.mjs /resellers*`.
 */

/* ── Enums ──────────────────────────────────────────────────────────────── */

export type ResellerStatus = 'active' | 'inactive' | 'suspended';
export type ResellerTier = 'bronze' | 'silver' | 'gold' | 'platinum';

/* ── Types ──────────────────────────────────────────────────────────────── */

export interface ResellerCustomerBrief {
  customer_id: number;
  customer_name: string;
  customer_email: string;
}

/** `GET /resellers` and `GET /resellers/:id` row (verified live). */
export interface ResellerRow {
  reseller_id: number;
  reseller_name: string;
  reseller_tier: ResellerTier | string;
  reseller_status: ResellerStatus | string;
  customer: ResellerCustomerBrief | null;
  organization_count: number;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

/** `GET /resellers/dashboard` result (verified live). */
export interface ResellerDashboard {
  total_resellers: number;
  total_customers: number;
  total_active_services: number;
  total_mrr: number;
  total_revenue: number;
  top_resellers: TopReseller[];
}

export interface TopReseller {
  reseller_id: number;
  reseller_name: string;
  reseller_tier: string;
  customer_count: number;
}

/** `GET /resellers/:id/stats` result (verified live). */
export interface ResellerStats {
  customer_count: number;
  active_customers: number;
  total_services: number;
  active_services: number;
  mrr: number;
  total_revenue: number;
  pending_revenue: number;
}

/** `GET /resellers/:id/customers` row (verified live). */
export interface ResellerCustomerRow {
  id: number;
  reseller_id: number;
  customer_id: number;
  customer_name: string;
  customer_email: string;
  status: string;
  joined_at: string;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

/** `GET /resellers/:id/services` row (verified live). */
export interface ResellerServiceRow {
  service_id: number;
  service_name: string;
  service_status: string;
  price_recurring: number;
  customer_id: number;
  customer_name: string;
  customer_email: string;
  created_at: string;
}

/** `GET /resellers/:id/invoices` row (verified live). */
export interface ResellerInvoiceRow {
  invoice_id: number;
  invoice_status: string;
  invoice_total: number;
  due_date: string | null;
  customer_id: number;
  customer_name: string;
  customer_email: string;
  created_at: string;
}

/* ── Params ─────────────────────────────────────────────────────────────── */

export interface ResellerListParams {
  page?: number;
  page_size?: number;
  q?: string;
  filters?: Record<string, unknown>;
}

function listQuery(params: ResellerListParams): string {
  const { filters, ...rest } = params;
  const out: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(rest)) {
    if (v !== undefined && v !== '') out[k] = v as string | number;
  }
  if (filters && Object.keys(filters).length > 0) out.filters = toFilters(filters);
  return qs.stringify(out);
}

/* ── Query keys ──────────────────────────────────────────────────────────── */

export const resellerKeys = {
  all: ['resellers'] as const,
  list: (params: ResellerListParams) => ['resellers', 'list', params] as const,
  dashboard: () => ['resellers', 'dashboard'] as const,
  detail: (id: number) => ['resellers', 'detail', id] as const,
  stats: (id: number) => ['resellers', id, 'stats'] as const,
  customers: (id: number, params: ResellerListParams) => ['resellers', id, 'customers', params] as const,
  services: (id: number, params: ResellerListParams) => ['resellers', id, 'services', params] as const,
  invoices: (id: number, params: ResellerListParams) => ['resellers', id, 'invoices', params] as const,
};

/* ── List & dashboard ────────────────────────────────────────────────────── */

/** `GET /resellers` — paginated reseller list. */
export function useResellers(params: ResellerListParams = {}) {
  return useQuery({
    queryKey: resellerKeys.list(params),
    queryFn: async (): Promise<ResponseArray<ResellerRow>> => {
      const res = await dataCentralApi.get<ResponseArray<ResellerRow>>(
        `/resellers?${listQuery({ page: 1, page_size: 20, ...params })}`,
      );
      return res.data;
    },
  });
}

/** `GET /resellers/dashboard` — global dashboard stats. */
export function useResellerDashboard() {
  return useQuery({
    queryKey: resellerKeys.dashboard(),
    queryFn: async (): Promise<ResponseObject<ResellerDashboard>> => {
      const res = await dataCentralApi.get<ResponseObject<ResellerDashboard>>('/resellers/dashboard');
      return res.data;
    },
    staleTime: 60_000,
  });
}

/* ── Detail ──────────────────────────────────────────────────────────────── */

/** `GET /resellers/:id` — single reseller record. */
export function useResellerDetail(id: number) {
  return useQuery({
    queryKey: resellerKeys.detail(id),
    enabled: id > 0,
    queryFn: async (): Promise<ResponseObject<ResellerRow>> => {
      const res = await dataCentralApi.get<ResponseObject<ResellerRow>>(`/resellers/${id}`);
      return res.data;
    },
  });
}

/** `GET /resellers/:id/stats` — per-reseller performance metrics. */
export function useResellerStats(id: number) {
  return useQuery({
    queryKey: resellerKeys.stats(id),
    enabled: id > 0,
    queryFn: async (): Promise<ResponseObject<ResellerStats>> => {
      const res = await dataCentralApi.get<ResponseObject<ResellerStats>>(`/resellers/${id}/stats`);
      return res.data;
    },
    staleTime: 60_000,
  });
}

/** `GET /resellers/:id/customers` — customers linked to this reseller. */
export function useResellerCustomers(id: number, params: ResellerListParams = {}) {
  return useQuery({
    queryKey: resellerKeys.customers(id, params),
    enabled: id > 0,
    queryFn: async (): Promise<ResponseArray<ResellerCustomerRow>> => {
      const res = await dataCentralApi.get<ResponseArray<ResellerCustomerRow>>(
        `/resellers/${id}/customers?${listQuery({ page: 1, page_size: 20, ...params })}`,
      );
      return res.data;
    },
  });
}

/** `GET /resellers/:id/services` — services from reseller's customers. */
export function useResellerServices(id: number, params: ResellerListParams = {}) {
  return useQuery({
    queryKey: resellerKeys.services(id, params),
    enabled: id > 0,
    queryFn: async (): Promise<ResponseArray<ResellerServiceRow>> => {
      const res = await dataCentralApi.get<ResponseArray<ResellerServiceRow>>(
        `/resellers/${id}/services?${listQuery({ page: 1, page_size: 20, ...params })}`,
      );
      return res.data;
    },
  });
}

/** `GET /resellers/:id/invoices` — invoices from reseller's customers. */
export function useResellerInvoices(id: number, params: ResellerListParams = {}) {
  return useQuery({
    queryKey: resellerKeys.invoices(id, params),
    enabled: id > 0,
    queryFn: async (): Promise<ResponseArray<ResellerInvoiceRow>> => {
      const res = await dataCentralApi.get<ResponseArray<ResellerInvoiceRow>>(
        `/resellers/${id}/invoices?${listQuery({ page: 1, page_size: 20, ...params })}`,
      );
      return res.data;
    },
  });
}

/* ── Mutations ───────────────────────────────────────────────────────────── */

export interface CreateResellerPayload {
  customer_id: number;
  reseller_name: string;
  reseller_tier?: string;
}

/** `POST /resellers` — create a new reseller. */
export function useCreateReseller() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateResellerPayload): Promise<ResponseObject<ResellerRow>> => {
      const res = await dataCentralApi.post<ResponseObject<ResellerRow>>('/resellers', payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: resellerKeys.all });
    },
  });
}

/** `PUT /resellers/:id` body — backend `UpdateResellerRequest` (all optional). */
export interface UpdateResellerPayload {
  reseller_name?: string;
  reseller_tier?: string;
  reseller_status?: string;
}

/** `PUT /resellers/:id` — update reseller name/tier/status (inline edit). */
export function useUpdateReseller(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateResellerPayload): Promise<ResponseObject<ResellerRow>> => {
      const res = await dataCentralApi.put<ResponseObject<ResellerRow>>(`/resellers/${id}`, payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: resellerKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: resellerKeys.all });
    },
  });
}

/** `DELETE /resellers/:id` — delete a reseller. */
export function useDeleteReseller() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await dataCentralApi.delete(`/resellers/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: resellerKeys.all });
    },
  });
}

export interface AddResellerCustomerPayload {
  customer_id: number;
  status?: string;
}

/** `POST /resellers/:id/customers` — link a customer to a reseller. */
export function useAddResellerCustomer(resellerId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: AddResellerCustomerPayload): Promise<ResponseObject<ResellerCustomerRow>> => {
      const res = await dataCentralApi.post<ResponseObject<ResellerCustomerRow>>(
        `/resellers/${resellerId}/customers`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: resellerKeys.customers(resellerId, {}) });
    },
  });
}

/** `DELETE /resellers/:id/customers/:customerId` — unlink a customer. */
export function useRemoveResellerCustomer(resellerId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (customerId: number): Promise<void> => {
      await dataCentralApi.delete(`/resellers/${resellerId}/customers/${customerId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: resellerKeys.customers(resellerId, {}) });
    },
  });
}
